import { css } from '@emotion/react';
import { notification, Skeleton } from 'antd';
import Router from 'next/router';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import Error from '@/pages/_error';
import Button from '@/src/shared/components/Button';
import Divider from '@/src/shared/components/Divider';
import Form from '@/src/shared/components/Form';
import { checkAccess, COMPLIANCE_ACCESS } from '@/src/shared/constants';
import request from '@/utils/axios';

import HeaderSection from '../../HeaderSection';
import StickyFooter from '../../StickyFooter';
import BLOCK_ID_MAP from '../constants/blockMap';
import CompliAssistCharts from './CompliAssistCharts';
import DailyDigest from './DailyDigest';
import GeneralPublications from './GeneralPublications';
import SmartBrief from './SmartBrief';
import StateNet from './StateNet';
import StatePublications from './StatePublications';

const handleExit = (url, overwrite, form) => {
  const touched = form.isFieldsTouched();

  if (
    !overwrite &&
    touched &&
    Router.pathname !== url &&
    url !== '/account/update-profile/submission-confirmation/' &&
    !window.confirm('You may lose unsaved changes if you leave this page')
  ) {
    Router.events.emit('routeChangeError');
    // eslint-disable-next-line no-throw-literal
    throw 'Abort route change.';
  }
};

const onUnload = e => {
  // the method that will be used for both add and remove event
  // eslint-disable-next-line no-param-reassign
  const _e = e || window.event;
  const confirmationMessage = 'You may lose unsaved changes if you leave this page.';
  _e.returnValue = confirmationMessage;
  return confirmationMessage;
};

const styles = {
  subscriptionContainer: css`
    padding: 2.6rem 2.4rem 0 2.4rem;
  `,
};

const INITIAL_PREFERENCES_STATE = {
  DigestScopeId: null,
  DigestFormatId: 1,
  Jurisdictions: [],
  LinesOfBusiness: [],
  GeneralPublications: [],
  StatePublications: [],
  CompliAssistCharts: [],
};

const INITIAL_FIELD_OPTIONS_STATE = {
  DigestFormats: [],
  DigestScopes: [],
  GeneralPublications: [],
  CompliAssistCharts: [],
  LinesOfBusiness: [],
  StatePublications: {
    States: [],
    Titles: [],
  },
  Jurisdictions: [],
};
const SubscriptionManagement = props => {
  const { form, pagedata, personId, role } = props;

  const [options, setOptions] = useState(INITIAL_FIELD_OPTIONS_STATE);
  const [exitOverwrite, setExitOverwrite] = useState(false);

  const [preferences, setPreferences] = useState(INITIAL_PREFERENCES_STATE);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(true);
  const [isLoadingFieldOptions, setIsLoadingFieldOptions] = useState(true);
  const [manuallyTouched, setManuallyTouched] = useState(false)

  const blockThree = pagedata.getBlockDataById(BLOCK_ID_MAP.blockThree);
  const blockThreeComponents = pagedata.getComponentsDataByBlockId(BLOCK_ID_MAP.blockThree);

  const blockFour = pagedata.getBlockDataById(BLOCK_ID_MAP.blockFour);
  const blockFourComponents = pagedata.getComponentsDataByBlockId(BLOCK_ID_MAP.blockFour);

  const blockFive = pagedata.getBlockDataById(BLOCK_ID_MAP.blockFive);
  const blockFiveComponents = pagedata.getComponentsDataByBlockId(BLOCK_ID_MAP.blockFive);

  const blockSix = pagedata.getBlockDataById(BLOCK_ID_MAP.blockSix);
  const blockSixComponents = pagedata.getComponentsDataByBlockId(BLOCK_ID_MAP.blockSix);

  const blockSeven = pagedata.getBlockDataById(BLOCK_ID_MAP.blockSeven);
  const blockSevenComponents = pagedata.getComponentsDataByBlockId(BLOCK_ID_MAP.blockSeven);

  const blockEight = pagedata.getBlockDataById(BLOCK_ID_MAP.blockEight);
  const blockEightComponents = pagedata.getComponentsDataByBlockId(BLOCK_ID_MAP.blockEight);

  const hasComplianceChartAccess = checkAccess(COMPLIANCE_ACCESS.SUBSCRIPTIONS, role);

  const fetchOptions = async () => {
    const response = await request({
      url: `profile/subscriptionoptions`,
      params: {
        personId,
      },
      method: 'GET',
      triggerLoad: false,
    });
    setIsLoadingFieldOptions(false);
    const {
      data: { FieldOptions },
    } = response;
    setOptions({
      DigestFormats: FieldOptions.DigestFormats,
      DigestScopes: FieldOptions.DigestScopes,
      GeneralPublications: FieldOptions.GeneralPublications,
      CompliAssistCharts: FieldOptions.CompliAssistCharts,
      LinesOfBusiness: FieldOptions.LinesOfBusiness,
      Jurisdictions: FieldOptions.Jurisdictions,
      StatePublications: FieldOptions.StatePublications,
    });
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  useEffect(() => {
    if (personId) {
      fetchProfile();
    }
  }, [personId]);

  useEffect(() => {
    const handleRouteChange = url => {
      handleExit(url, exitOverwrite, form);
    };

    if (!exitOverwrite) {
      Router.events.on('routeChangeStart', handleRouteChange);
      window.addEventListener('beforeunload', onUnload);
    } else {
      Router.events.off('routeChangeStart', handleRouteChange);
      window.removeEventListener('beforeunload', onUnload);
    }

    return () => {
      Router.events.off('routeChangeStart', handleRouteChange);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [form, exitOverwrite]);

  const touched = form.isFieldsTouched() || manuallyTouched

  // eslint-disable-next-line consistent-return
  const fetchProfile = async () => {
    const response = await request({
      url: '/profile/details',
      method: 'GET',
      params: {
        personId,
      },
      triggerLoad: false,
    });
    setIsLoadingPreferences(false);
    const { data } = response;
    if (data.Success && data.Success === 'false') {
      return <Error />;
    }

    setPreferences({
      DigestScopeId: data.DigestScopeId,
      DigestFormatId: data.DigestFormatId,
      Jurisdictions: data.Jurisdictions.map(j => j.StateCode),
      LinesOfBusiness: data.LinesOfBusiness.map(l => l.LobCode),
      GeneralPublications: data.GeneralPublications.map(g => g.PublicationCode),
      StatePublications: {
        States: data.StatePublications.States.map(s => s.StateCode),
        Titles: data.StatePublications.Titles.map(t => t.PublicationCode),
      },
      CompliAssistCharts: data.CompliAssistCharts.map(c => c.ChartId),
    });
  };

  const handleSave = async e => {
    if (e && 'preventDefault' in e) {
      e.preventDefault();
    }

    await form.validateFieldsAndScroll(async (err, values) => {
      if (err) return;

      const payload = {
        PersonId: personId,
        DigestScopeId: values.DigestScopeId,
        DigestFormatId: values.DigestFormatId,
        Jurisdictions: values.Jurisdictions
          ? values.Jurisdictions.map(jurisdiction => {
            return {
              StateCode: jurisdiction,
            };
          })
          : [],
        LinesOfBusiness: values.LinesOfBusiness
          ? values.LinesOfBusiness.map(b => {
            return {
              LobCode: b,
            };
          })
          : [],
        GeneralPublications: values.GeneralPublications
          ? values.GeneralPublications.map(pub => {
            return {
              PublicationCode: pub,
            };
          })
          : [],
        StatePublications: {
          States: values.StatePublicationsStates
            ? values.StatePublicationsStates.map(s => {
              return {
                StateCode: s,
              };
            })
            : [],
          Titles: values.StatePublicationsTitles
            ? values.StatePublicationsTitles.map(pub => {
              return {
                PublicationCode: pub,
              };
            })
            : [],
        },
        CompliAssistCharts: values.CompliAssistCharts
          ? values.CompliAssistCharts.map(c => {
            return {
              ChartId: c,
            };
          })
          : [],
      };

      const response = await request({
        url: 'profile/updatesubscriptions',
        method: 'POST',
        data: payload,
        triggerLoad: false,
      });
      const { data } = response;
      if (data.Success === 'true') {
        notification.success({ message: data.Message });
      }
    });
    setExitOverwrite(true);
  };

  return (
    <Form
      onSubmit={handleSave}
      css={styles.subscriptionContainer}
      onChange={() => setExitOverwrite(false)}
    >
      {isLoadingPreferences || isLoadingFieldOptions ? (
        <Skeleton paragraph={{ rows: 12 }} active />
      ) : (
        <div>
          <HeaderSection headerText="My Subscriptions" />
          <DailyDigest
            form={form}
            options={options}
            profile={preferences}
            designData={{ block: blockThree, blockComponents: blockThreeComponents }}
            handleSave={handleSave}
          />
          <Divider />
          <StatePublications
            form={form}
            options={options.StatePublications}
            profile={preferences}
            designData={{ block: blockSeven, blockComponents: blockSevenComponents }}
            setManuallyTouched={setManuallyTouched}
          />
          <Divider />
          <StateNet designData={{ block: blockFive, blockComponents: blockFiveComponents }} />
          <Divider />
          <GeneralPublications
            form={form}
            publications={options.GeneralPublications}
            profile={preferences}
            designData={{ block: blockSix, blockComponents: blockSixComponents }}
            setManuallyTouched={setManuallyTouched}
          />
          <Divider />
          {hasComplianceChartAccess ? (
            <>
              <CompliAssistCharts
                form={form}
                charts={options.CompliAssistCharts}
                profile={preferences}
                designData={{ block: blockEight, blockComponents: blockEightComponents }}
                setManuallyTouched={setManuallyTouched}
              />
              <Divider />
            </>
          ) : null}
          <SmartBrief designData={{ block: blockFour, blockComponents: blockFourComponents }} />
          <StickyFooter>
            <Form.Item>
              <Button
                disabled={!touched}
                size="small"
                theme={touched ? 'darkOutlined' : 'disabled'}
                htmlType="submit"
                css={styles.buttonStyle}
              >
                Save
              </Button>
            </Form.Item>
          </StickyFooter>
        </div>
      )}
    </Form>
  );
};

SubscriptionManagement.propTypes = {};

const mapStateToProps = state => {
  const {
    auth: {
      userinfo: { PersonID },
      role,
    },
  } = state;

  return {
    personId: PersonID,
    role,
  };
};

export default connect(
  mapStateToProps,
  null
)(Form.create()(SubscriptionManagement));
